export const DOWNLOAD_SCHEDULING_MODE = Object.freeze({
  STRICT: "strict",
  PRIORITY: "priority"
});

const VALID_MODES = new Set(Object.values(DOWNLOAD_SCHEDULING_MODE));
const LOCAL_OVERRIDE_KEY = "dvDownloadSchedulingMode";
let runtimeDefaultMode = DOWNLOAD_SCHEDULING_MODE.PRIORITY;

/**
 * Normalizes scheduling mode to a supported value.
 * @param {string} mode
 * @returns {string}
 */
export const normalizeDownloadSchedulingMode = mode => {
  if (typeof mode !== "string") {
    return DOWNLOAD_SCHEDULING_MODE.PRIORITY;
  }
  const normalized = mode.trim().toLowerCase();
  return VALID_MODES.has(normalized)
    ? normalized
    : DOWNLOAD_SCHEDULING_MODE.PRIORITY;
};

/**
 * Sets runtime default scheduling mode.
 * @param {string} mode
 * @returns {string}
 */
export const setDownloadSchedulingRuntimeDefaultMode = mode => {
  runtimeDefaultMode = normalizeDownloadSchedulingMode(mode);
  return runtimeDefaultMode;
};

/**
 * Returns runtime default scheduling mode.
 * @returns {string}
 */
export const getDownloadSchedulingRuntimeDefaultMode = () => runtimeDefaultMode;

// Accept an explicit local override while ignoring malformed/stale values.
/**
 * Reads valid scheduling override from localStorage.
 * @returns {string|null}
 */
const readLocalOverrideMode = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const rawMode = window.localStorage.getItem(LOCAL_OVERRIDE_KEY);
    if (!rawMode) {
      return null;
    }
    const normalized = normalizeDownloadSchedulingMode(rawMode);
    return normalized !== DOWNLOAD_SCHEDULING_MODE.PRIORITY ||
      rawMode.trim().toLowerCase() === DOWNLOAD_SCHEDULING_MODE.PRIORITY
      ? normalized
      : null;
  } catch (_) {
    return null;
  }
};

/**
 * Returns effective scheduling mode considering local override policy.
 * @param {Object} [options]
 * @returns {string}
 */
export const getDefaultDownloadSchedulingMode = (options = {}) => {
  const allowLocalOverride = options.allowLocalOverride !== false;
  if (allowLocalOverride) {
    const override = readLocalOverrideMode();
    if (override) {
      return override;
    }
  }
  return runtimeDefaultMode;
};
